'use client'

import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../../../../lib/cn'

export interface GridCrosshatchCenterProps {
  /**
   * Enable entrance animation
   * @default true
   */
  animate?: boolean

  /**
   * Animation delay in seconds (allows hero content to load first)
   * @default 0.5
   */
  animationDelay?: number

  className?: string
}

/**
 * GridCrosshatchCenter - Multi-angle hatching pattern with center fade
 *
 * Dense hatching with lines at multiple angles creating a sophisticated
 * pen-stroke texture that fades from the center.
 *
 * Fixed fadeSize: 0.50
 *
 * @example
 * ```tsx
 * <section className="relative">
 *   <GridCrosshatchCenter />
 *   <div className="relative z-10">Content here</div>
 * </section>
 * ```
 */
export function GridCrosshatchCenter({
  animate = true,
  animationDelay = 0.5,
  className,
}: GridCrosshatchCenterProps = {}) {
  const prefersReducedMotion = useReducedMotion()
  const fadeSize = 0.5
  const size = `${fadeSize * 100}%`
  const maskGradient = `radial-gradient(ellipse ${size} ${size} at 50% 50%, #000 20%, transparent 100%)`

  return (
    <motion.div
      className={cn('absolute inset-0 z-0', className)}
      style={{
        backgroundImage: `
          repeating-linear-gradient(22.5deg, transparent, transparent 1px, color-mix(in srgb, var(--border), transparent 65%) 1px, color-mix(in srgb, var(--border), transparent 65%) 2px, transparent 2px, transparent 8px),
          repeating-linear-gradient(67.5deg, transparent, transparent 1px, color-mix(in srgb, var(--border), transparent 65%) 1px, color-mix(in srgb, var(--border), transparent 65%) 2px, transparent 2px, transparent 8px),
          repeating-linear-gradient(112.5deg, transparent, transparent 1px, color-mix(in srgb, var(--border), transparent 65%) 1px, color-mix(in srgb, var(--border), transparent 65%) 2px, transparent 2px, transparent 8px),
          repeating-linear-gradient(157.5deg, transparent, transparent 1px, color-mix(in srgb, var(--border), transparent 65%) 1px, color-mix(in srgb, var(--border), transparent 65%) 2px, transparent 2px, transparent 8px)
        `,
        maskImage: maskGradient,
        WebkitMaskImage: maskGradient,
      }}
      initial={animate ? { opacity: 0 } : false}
      animate={animate ? { opacity: 1 } : false}
      transition={{
        duration: prefersReducedMotion ? 0.3 : 1.2,
        delay: animate ? (prefersReducedMotion ? 0 : animationDelay) : 0,
        ease: [0.16, 1, 0.3, 1],
      }}
    />
  )
}
